/**
 * Normalize Access API payloads into one frontend-safe RBAC shape.
 *
 * @postcondition Returned roles/admins/effective profiles always use arrays/objects instead of nullish backend holes.
 * @mutates none
 */

const normalizeBooleanFlag = (value, defaultValue = false) => {
  if (value === null || value === undefined || value === "") return defaultValue
  if (typeof value === "boolean") return value
  if (typeof value === "number") return value === 1
  if (typeof value === "string") {
    const normalizedValue = value.trim().toLowerCase()
    if (["1", "true", "yes", "y"].includes(normalizedValue)) return true
    if (["0", "false", "no", "n"].includes(normalizedValue)) return false
  }
  return Boolean(value)
}

const hasExplicitBooleanFlagValue = (value) =>
  value !== null && value !== undefined && value !== ""

const ACCESS_ROLE_DECISIONS = new Set(["allow", "deny"])

const hasOwn = (value, key) =>
  Object.prototype.hasOwnProperty.call(value || {}, key)

export function normalizeAccessCapabilityGroups(payload) {
  const groups = payload?.groups
  const capabilities = payload?.capabilities
  if (!Array.isArray(groups) || !Array.isArray(capabilities)) {
    throw new TypeError(
      "capability catalogue groups and capabilities must be arrays"
    )
  }

  const groupsByCode = new Map()
  const normalizedGroups = groups.map((group) => {
    if (!group || typeof group !== "object" || Array.isArray(group)) {
      throw new TypeError("capability catalogue contains an invalid group")
    }
    const { code, title, order } = group
    if (
      typeof code !== "string" ||
      !code.trim() ||
      typeof title !== "string" ||
      !title.trim() ||
      !Number.isFinite(Number(order)) ||
      groupsByCode.has(code)
    ) {
      throw new TypeError(
        "capability catalogue contains an invalid group identity"
      )
    }
    const normalized = {
      code,
      title,
      order: Number(order),
      capabilities: [],
    }
    groupsByCode.set(code, normalized)
    return normalized
  })

  const capabilityCodes = new Set()
  capabilities.forEach((capability) => {
    if (
      !capability ||
      typeof capability !== "object" ||
      Array.isArray(capability)
    ) {
      throw new TypeError("capability catalogue contains an invalid capability")
    }
    const { code, group_code: groupCode, title, description } = capability
    if (
      typeof code !== "string" ||
      !code.trim() ||
      typeof groupCode !== "string" ||
      !groupsByCode.has(groupCode) ||
      typeof title !== "string" ||
      !title.trim() ||
      typeof description !== "string" ||
      !description.trim() ||
      capabilityCodes.has(code)
    ) {
      throw new TypeError(
        "capability catalogue contains an invalid capability identity"
      )
    }
    capabilityCodes.add(code)
    groupsByCode.get(groupCode).capabilities.push({
      code,
      group_code: groupCode,
      title,
      description,
      module_scoped: normalizeBooleanFlag(
        capability.module_scoped ?? capability.moduleScoped,
        false
      ),
      minimum_module_access:
        capability.minimum_module_access ??
        capability.minimumModuleAccess ??
        null,
    })
  })

  normalizedGroups.forEach((group) => {
    group.capabilities.sort((left, right) =>
      left.code.localeCompare(right.code)
    )
  })

  const packsById = new Map()
  const addPack = (pack) => {
    const id = Number(pack?.id ?? pack?.pack_id ?? pack?.module_pack_id) || 0
    if (id <= 0) return
    const label =
      pack?.label || pack?.title || pack?.module_pack_title || `Пакет ${id}`
    const existing = packsById.get(id)
    if (!existing) {
      packsById.set(id, { id, label })
      return
    }
    if (existing.label === `Пакет ${id}` && label !== `Пакет ${id}`) {
      existing.label = label
    }
  }
  ;(Array.isArray(payload.packs) ? payload.packs : []).forEach(addPack)

  const rawModules = Array.isArray(payload.modules) ? payload.modules : []
  const modulesById = new Map()
  rawModules.forEach((module) => {
    const id = Number(module?.id ?? module?.module_id) || 0
    const packId = Number(module?.pack_id ?? module?.module_pack_id) || 0
    addPack({
      id: packId,
      label: module?.pack_label,
      title: module?.pack_title,
      module_pack_title: module?.module_pack_title,
    })
    const normalized = {
      id,
      pack_id: packId,
      label:
        module?.label ||
        module?.title ||
        module?.module_title ||
        `Модуль ${module?.id ?? module?.module_id ?? ""}`,
    }
    if (hasOwn(module, "description") || hasOwn(module, "module_description")) {
      normalized.description =
        module?.description ?? module?.module_description ?? ""
    }
    if (hasOwn(module, "icon") || hasOwn(module, "module_icon")) {
      normalized.icon = module?.icon ?? module?.module_icon ?? null
    }
    if (Array.isArray(module?.capabilities)) {
      normalized.capabilities = module.capabilities
        .map((capability) => ({
          code: capability?.code,
          title: capability?.title || capability?.code || "",
          description: capability?.description || "",
          group_code: capability?.group_code || "catalog",
          module_scoped: normalizeBooleanFlag(
            capability?.module_scoped ?? capability?.moduleScoped,
            false
          ),
          minimum_module_access:
            capability?.minimum_module_access ??
            capability?.minimumModuleAccess ??
            null,
        }))
        .filter(
          (capability) =>
            typeof capability.code === "string" && capability.code.trim()
        )
    }
    if (id <= 0) return
    const existing = modulesById.get(id)
    if (!existing) {
      modulesById.set(id, normalized)
      return
    }
    const capabilities = [
      ...(existing.capabilities || []),
      ...(normalized.capabilities || []),
    ]
    existing.capabilities = capabilities.filter(
      (capability, index, rows) =>
        rows.findIndex((candidate) => candidate.code === capability.code) ===
        index
    )
  })
  const modules = [...modulesById.values()]

  const memberships = []
  const membershipKeys = new Set()
  const addMembership = (membership) => {
    const packId =
      Number(membership?.pack_id ?? membership?.module_pack_id) || 0
    const moduleId = Number(membership?.module_id) || 0
    if (packId <= 0 || moduleId <= 0) return
    const key = `${moduleId}:${packId}`
    if (membershipKeys.has(key)) return
    membershipKeys.add(key)
    memberships.push({ pack_id: packId, module_id: moduleId })
  }
  ;(Array.isArray(payload.memberships) ? payload.memberships : []).forEach(
    addMembership
  )
  // Some backend revisions expose aliases on each module rather than as rows.
  // Convert both spellings into the same canonical membership ABI.
  modules.forEach((module) => {
    addMembership({ pack_id: module.pack_id, module_id: module.id })
  })
  rawModules.forEach((module) => {
    const moduleId = Number(module?.id ?? module?.module_id) || 0
    const aliases = module?.module_pack_ids ?? module?.pack_ids
    if (Array.isArray(aliases)) {
      aliases.forEach((packId) =>
        addMembership({ pack_id: packId, module_id: moduleId })
      )
    }
  })

  const normalized = normalizedGroups
  Object.defineProperties(normalized, {
    groups: { value: normalizedGroups, enumerable: false },
    packs: { value: [...packsById.values()], enumerable: false },
    modules: { value: modules, enumerable: false },
    memberships: { value: memberships, enumerable: false },
    capability_metadata: {
      value:
        payload.capability_metadata &&
        typeof payload.capability_metadata === "object"
          ? payload.capability_metadata
          : {},
      enumerable: false,
    },
    runtime_only: {
      value: Array.isArray(payload.runtime_only) ? payload.runtime_only : [],
      enumerable: false,
    },
  })
  return normalized
}

const assertDecision = (decision, field) => {
  if (!ACCESS_ROLE_DECISIONS.has(decision)) {
    throw new TypeError(`${field} must be allow or deny`)
  }
  return decision
}

const normalizeGroupDecisionMap = (value) => {
  if (value === null || value === undefined) return {}
  if (typeof value !== "object" || Array.isArray(value)) {
    throw new TypeError("groupDecisions must be an object")
  }

  const normalized = {}
  Object.entries(value).forEach(([groupCode, decision]) => {
    if (!groupCode.trim()) {
      throw new TypeError("capability group code must be non-empty")
    }
    normalized[groupCode] = assertDecision(
      decision,
      `groupDecisions.${groupCode}`
    )
  })

  return normalized
}

const normalizeCapabilityOverrideMap = (value) => {
  if (value === null || value === undefined) return {}
  if (typeof value !== "object" || Array.isArray(value)) {
    throw new TypeError("capabilityOverrides must be an object")
  }

  const normalized = {}
  Object.entries(value).forEach(([capabilityCode, decision]) => {
    if (!capabilityCode.trim()) {
      throw new TypeError("capability override code must be non-empty")
    }
    if (hasOwn(normalized, capabilityCode)) {
      throw new TypeError(`duplicate capability override: ${capabilityCode}`)
    }
    normalized[capabilityCode] = assertDecision(
      decision,
      `capabilityOverrides.${capabilityCode}`
    )
  })

  return Object.fromEntries(
    Object.keys(normalized)
      .sort()
      .map((capabilityCode) => [capabilityCode, normalized[capabilityCode]])
  )
}

const normalizeRawGroupRules = (value) => {
  if (!Array.isArray(value) || value.length !== 8) {
    throw new TypeError(
      "capability_group_rules must contain exactly eight rules"
    )
  }

  const normalized = {}
  value.forEach((rule) => {
    if (!rule || typeof rule !== "object" || Array.isArray(rule)) {
      throw new TypeError("capability_group_rules contains an invalid rule")
    }
    const groupCode = rule.group_code
    if (typeof groupCode !== "string" || !groupCode.trim()) {
      throw new TypeError("capability group code must be non-empty")
    }
    if (hasOwn(normalized, groupCode)) {
      throw new TypeError(`duplicate capability group: ${groupCode}`)
    }
    normalized[groupCode] = assertDecision(
      rule.decision,
      `capability_group_rules.${groupCode}`
    )
  })

  if (Object.keys(normalized).length !== 8) {
    throw new TypeError("capability_group_rules is missing a required group")
  }

  return normalized
}

const normalizeRawCapabilityOverrides = (value) => {
  if (!Array.isArray(value)) {
    throw new TypeError("capability_overrides must be an array")
  }

  const normalized = {}
  value.forEach((override) => {
    if (!override || typeof override !== "object" || Array.isArray(override)) {
      throw new TypeError("capability_overrides contains an invalid override")
    }
    const capabilityCode = override.capability_code
    if (typeof capabilityCode !== "string" || !capabilityCode.trim()) {
      throw new TypeError("capability override code must be non-empty")
    }
    if (hasOwn(normalized, capabilityCode)) {
      throw new TypeError(`duplicate capability override: ${capabilityCode}`)
    }
    normalized[capabilityCode] = assertDecision(
      override.decision,
      `capability_overrides.${capabilityCode}`
    )
  })

  return Object.fromEntries(
    Object.keys(normalized)
      .sort()
      .map((capabilityCode) => [capabilityCode, normalized[capabilityCode]])
  )
}

/**
 * Adapt the strict raw role policy ABI to the matrix's canonical maps.
 * Missing raw policy fields are tolerated only for list rows that do not carry
 * policy details; once either raw array is present, both arrays are required.
 *
 * @mutates none
 */
export function normalizeAccessRolePolicy(role) {
  const policySource =
    role?.policy && typeof role.policy === "object" ? role.policy : role
  const hasRawGroupRules = hasOwn(policySource, "capability_group_rules")
  const hasRawOverrides = hasOwn(policySource, "capability_overrides")

  if (hasRawGroupRules || hasRawOverrides) {
    if (!hasRawGroupRules || !hasRawOverrides) {
      throw new TypeError("role policy must contain both raw arrays")
    }
    return {
      groupDecisions: normalizeRawGroupRules(
        policySource.capability_group_rules
      ),
      capabilityOverrides: normalizeRawCapabilityOverrides(
        policySource.capability_overrides
      ),
    }
  }

  return {
    groupDecisions: normalizeGroupDecisionMap(policySource?.groupDecisions),
    capabilityOverrides: normalizeCapabilityOverrideMap(
      policySource?.capabilityOverrides
    ),
  }
}

export function normalizeModuleCapabilityOverrides(value) {
  if (value === null || value === undefined) return []

  let rows = value
  if (!Array.isArray(value)) {
    if (typeof value !== "object") {
      throw new TypeError(
        "module_capability_overrides must be an array or keyed object"
      )
    }

    if (
      hasOwn(value, "module_id") &&
      hasOwn(value, "capability_code") &&
      hasOwn(value, "decision")
    ) {
      rows = [value]
    } else {
      rows = Object.entries(value).flatMap(([moduleId, decisions]) => {
        if (Array.isArray(decisions)) {
          return decisions.map((override) => ({
            ...override,
            module_id: override?.module_id ?? moduleId,
          }))
        }
        if (!decisions || typeof decisions !== "object") {
          throw new TypeError(
            "module_capability_overrides keyed values must be objects or arrays"
          )
        }
        if (
          hasOwn(decisions, "capability_code") ||
          hasOwn(decisions, "decision")
        ) {
          return [{ ...decisions, module_id: decisions.module_id ?? moduleId }]
        }
        return Object.entries(decisions).map(([capabilityCode, decision]) => ({
          module_id: moduleId,
          capability_code: capabilityCode,
          decision,
        }))
      })
    }
  }

  const seen = new Set()
  return rows
    .map((override) => {
      if (
        !override ||
        typeof override !== "object" ||
        Array.isArray(override)
      ) {
        throw new TypeError(
          "module_capability_overrides contains an invalid override"
        )
      }
      const moduleId = Number(override.module_id)
      const capabilityCode = override.capability_code
      if (!Number.isInteger(moduleId) || moduleId <= 0) {
        throw new TypeError(
          "module capability override module_id must be positive"
        )
      }
      if (typeof capabilityCode !== "string" || !capabilityCode.trim()) {
        throw new TypeError("module capability override code must be non-empty")
      }
      const key = `${moduleId}:${capabilityCode}`
      if (seen.has(key)) {
        throw new TypeError(`duplicate module capability override: ${key}`)
      }
      seen.add(key)
      return {
        module_id: moduleId,
        capability_code: capabilityCode,
        decision: assertDecision(override.decision, key),
      }
    })
    .sort(
      (left, right) =>
        left.module_id - right.module_id ||
        left.capability_code.localeCompare(right.capability_code)
    )
}

const isPlainObject = (value) => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false
  const prototype = Object.getPrototypeOf(value)
  return prototype === Object.prototype || prototype === null
}

const normalizeScopeMap = (value) => {
  if (!value || typeof value !== "object") return {}

  return Object.fromEntries(
    Object.entries(value).filter(([, accessLevel]) =>
      ["none", "view", "edit"].includes(accessLevel)
    )
  )
}

// PHP encodes an empty associative array as [], while a non-empty keyed map
// is an object. Only the empty transport form is canonicalized here; any
// non-empty array is malformed authority and must fail closed.
const normalizeInboundScopeMap = (value) => {
  if (Array.isArray(value)) return value.length === 0 ? {} : null
  if (value === null || value === undefined) return {}
  if (!isPlainObject(value)) return null

  const entries = Object.entries(value)
  if (
    entries.some(
      ([scopeId, accessLevel]) =>
        !/^[1-9]\d*$/.test(scopeId) ||
        !["none", "view", "edit"].includes(accessLevel)
    )
  ) {
    return null
  }

  return Object.fromEntries(entries)
}

const normalizeInboundModuleToPackMap = (value) => {
  if (Array.isArray(value)) return value.length === 0 ? {} : null
  if (value === null || value === undefined) return {}
  if (!isPlainObject(value)) return null

  const entries = Object.entries(value)
  if (
    entries.some(
      ([moduleId, packId]) =>
        !/^[1-9]\d*$/.test(moduleId) ||
        !Number.isInteger(Number(packId)) ||
        Number(packId) <= 0
    )
  ) {
    return null
  }

  return Object.fromEntries(entries)
}

/**
 * Normalize editable scope rows so only backend-owned explicit overrides survive.
 *
 * @precondition entries are UI/editor rows or other row-like scope payloads.
 * @postcondition Returns only positive numeric ids with `none`/`view`/`edit` access levels; invalid ids and inherited/unknown markers are dropped.
 * @mutates none
 */
export function normalizeExplicitAccessScopeRows(entries) {
  return (Array.isArray(entries) ? entries : [])
    .map((item) => ({
      id: Number(item?.id) || 0,
      access_level: item?.access_level,
    }))
    .filter(
      (item) =>
        item.id > 0 && ["none", "view", "edit"].includes(item.access_level)
    )
}

/**
 * Convert backend scope maps into canonical editor rows.
 *
 * @precondition value is a backend `{ id: access_level }` scope map or nullish.
 * @postcondition Returns normalized explicit editor rows owned by the access normalizer layer.
 * @mutates none
 */
export function accessScopeRowsFromMap(value) {
  return normalizeExplicitAccessScopeRows(
    Object.entries(value || {}).map(([id, access_level]) => ({
      id: Number(id),
      access_level,
    }))
  )
}

/**
 * Build the canonical Access role save payload from editor state.
 *
 * @precondition role is the current editor form state using canonical booleans/arrays.
 * @postcondition Returns only the backend policy ABI and explicit scope overrides; server-owned role flags are omitted.
 * @mutates none
 */
export function buildAccessRoleSavePayload(role) {
  const policy = normalizeAccessRolePolicy(role)
  const groupDecisions = normalizeGroupDecisionMap(policy.groupDecisions)
  if (Object.keys(groupDecisions).length !== 8) {
    throw new TypeError(
      "groupDecisions must contain the complete backend catalogue"
    )
  }
  const capabilityOverrides = normalizeCapabilityOverrideMap(
    policy.capabilityOverrides
  )
  const payload = {
    id: role?.id ?? null,
    code: role?.code || "",
    title: role?.title || "",
    description: role?.description || "",
    capability_group_rules: Object.entries(groupDecisions).map(
      ([groupCode, decision]) => ({ group_code: groupCode, decision })
    ),
    capability_overrides: Object.keys(capabilityOverrides).map(
      (capabilityCode) => ({
        capability_code: capabilityCode,
        decision: capabilityOverrides[capabilityCode],
      })
    ),
    module_capability_overrides: normalizeModuleCapabilityOverrides(
      role?.module_capability_overrides ??
        role?.policy?.module_capability_overrides
    ),
    pack_scopes: normalizeExplicitAccessScopeRows(role?.pack_scopes),
    module_scopes: normalizeExplicitAccessScopeRows(role?.module_scopes),
  }

  return payload
}

/**
 * Normalize one effective access profile snapshot.
 *
 * @param {object|null|undefined} profile
 * @returns {object|null}
 */
export function normalizeAccessProfile(profile) {
  if (!profile || typeof profile !== "object" || Array.isArray(profile)) {
    return null
  }

  const permissionsVersion = Number(profile.permissions_version)
  if (!Number.isFinite(permissionsVersion) || permissionsVersion <= 0) {
    return null
  }

  const rawScopes = profile.scopes
  if (
    rawScopes !== null &&
    rawScopes !== undefined &&
    !isPlainObject(rawScopes)
  ) {
    return null
  }

  const normalizedPacks = normalizeInboundScopeMap(rawScopes?.packs)
  const normalizedModules = normalizeInboundScopeMap(rawScopes?.modules)
  const normalizedModuleToPack = normalizeInboundModuleToPackMap(
    profile.module_to_pack
  )
  if (
    normalizedPacks === null ||
    normalizedModules === null ||
    normalizedModuleToPack === null
  ) {
    return null
  }

  return {
    ...profile,
    role_code: profile.role_code || "",
    role_title: profile.role_title || "",
    source: profile.source || "",
    permissions_version: permissionsVersion,
    is_superadmin: normalizeBooleanFlag(profile.is_superadmin, false),
    capabilities:
      profile.capabilities && typeof profile.capabilities === "object"
        ? profile.capabilities
        : {},
    reports: Array.isArray(profile.reports) ? profile.reports : [],
    scopes: {
      packs: normalizedPacks,
      modules: normalizedModules,
    },
    module_to_pack: normalizedModuleToPack,
    module_capability_overrides: normalizeModuleCapabilityOverrides(
      profile.module_capability_overrides ??
        profile.policy?.module_capability_overrides
    ),
  }
}

/**
 * Normalize one role row/details payload from `/api/access/roles*`.
 *
 * @param {object|null|undefined} role
 * @returns {object|null}
 */
export function normalizeAccessRole(role) {
  if (!role || typeof role !== "object") return null

  const hasIsActive = hasExplicitBooleanFlagValue(role.is_active)

  const normalizedRole = { ...role }
  delete normalizedRole.capability_codes
  const policy = normalizeAccessRolePolicy(role)

  return {
    ...normalizedRole,
    id: Number(role.id || 0),
    code: role.code || "",
    title: role.title || "",
    description: role.description || "",
    is_system: normalizeBooleanFlag(role.is_system, false),
    is_superadmin: normalizeBooleanFlag(role.is_superadmin, false),
    is_active: normalizeBooleanFlag(role.is_active, true),
    has_is_active: hasIsActive,
    users_count: Number(role.users_count || 0),
    capabilities_count: Number(role.capabilities_count || 0),
    groupDecisions: policy.groupDecisions,
    capabilityOverrides: policy.capabilityOverrides,
    module_capability_overrides: normalizeModuleCapabilityOverrides(
      role.module_capability_overrides ??
        role.policy?.module_capability_overrides
    ),
    pack_scopes: normalizeScopeMap(role.pack_scopes),
    module_scopes: normalizeScopeMap(role.module_scopes),
    effective_profile: normalizeAccessProfile(role.effective_profile),
  }
}

/**
 * Normalize one admin assignment row from `/api/access/admins*`.
 *
 * @param {object|null|undefined} admin
 * @returns {object|null}
 */
export function normalizeAccessAdmin(admin) {
  if (!admin || typeof admin !== "object") return null

  const adminWithoutOverrides = { ...admin }
  ;[
    ["report", "access", "mode"],
    ["report", "override", "names"],
  ].forEach((segments) => {
    delete adminWithoutOverrides[segments.join("_")]
  })

  return {
    ...adminWithoutOverrides,
    admin_id: Number(admin.admin_id || 0),
    role_id:
      admin.role_id === null ||
      admin.role_id === undefined ||
      admin.role_id === ""
        ? null
        : Number(admin.role_id),
    effective_profile: normalizeAccessProfile(admin.effective_profile),
  }
}

export function normalizeAccessRolesList(payload) {
  return Array.isArray(payload)
    ? payload.map(normalizeAccessRole).filter(Boolean)
    : []
}

export function normalizeAccessAdminsPayload(payload) {
  return {
    admins: Array.isArray(payload?.admins)
      ? payload.admins.map(normalizeAccessAdmin).filter(Boolean)
      : [],
    roles: normalizeAccessRolesList(payload?.roles || []),
  }
}

export function normalizeAccessAuditItems(items) {
  return Array.isArray(items)
    ? items.filter((item) => item && typeof item === "object")
    : []
}
